import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Button, SlideOver } from "@teals/ui-web";
import {
  useCreateDataSourceMutation,
  useUpdateDataSourceMutation,
  useVerifyConnectionMutation,
  useAnalyzeSchemaMutation,
  type DsType,
  type SchemaField,
  type DataSource,
  type CreateDataSourceRequest,
  type VerifyConnectionResult,
  type FilterConfig,
} from "../../../store/api/dataSourcesApi";
import { DEFAULT_SCHEMA_JSON } from "../constants";
import { emptyFilterConfig, toFilterConfigPayload } from "../filterConfig";
import { WizardFooter, FooterActions, ErrorBanner } from "../styles";
import StepIndicator from "./StepIndicator";
import StepSelectType from "./StepSelectType";
import StepDefineSchema from "./StepDefineSchema";
import StepAIAttributes from "./StepAIAttributes";
import StepQueryFilters from "./StepQueryFilters";
import StepConnectionDetails from "./StepConnectionDetails";
import StepSelectProvider from "./StepSelectProvider";
import StepCredentials from "./StepCredentials";
import StepVerifyIndex from "./StepVerifyIndex";
import StepBYOSuccess from "./StepBYOSuccess";

interface CreateWizardProps {
  isOpen: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  onOpenDetail: (ds: DataSource, tab?: "overview" | "upload") => void;
}

const MANAGED_LABELS: Record<number, string> = {
  1: "Step 1 of 5 \u2014 Type",
  2: "Step 2 of 5 \u2014 Schema",
  3: "Step 3 of 5 \u2014 Field Attributes",
  4: "Step 4 of 5 \u2014 Query filters",
  5: "Step 5 of 5 \u2014 Connection",
};

const BYO_LABELS: Record<number, string> = {
  1: "Step 1 of 6 \u2014 Type",
  2: "Step 2 of 6 \u2014 Provider",
  3: "Step 3 of 6 \u2014 Credentials",
  4: "Step 4 of 6 \u2014 Verify",
  5: "Step 5 of 6 \u2014 Query filters",
  6: "Step 6 of 6 \u2014 Connection",
};

function schemaFieldNames(schema: unknown): string[] {
  if (!schema || typeof schema !== "object") return [];
  const fields = (schema as { fields?: { name?: string }[] }).fields;
  if (!Array.isArray(fields)) return [];
  return fields.map((f) => f.name).filter((n): n is string => Boolean(n));
}

function unwrapError(e: unknown): string {
  if (typeof e === "object" && e !== null && "data" in e) {
    return String((e as { data: { message?: string } }).data?.message ?? e);
  }
  return "Request failed";
}

const CreateWizard: React.FC<CreateWizardProps> = ({
  isOpen,
  onClose,
  apiBaseUrl,
  onOpenDetail,
}) => {
  const [createDs, { isLoading: isCreating }] = useCreateDataSourceMutation();
  const [updateDs, { isLoading: isUpdating }] = useUpdateDataSourceMutation();
  const [verifyConn, { isLoading: isVerifying }] =
    useVerifyConnectionMutation();
  const [analyzeSchema] = useAnalyzeSchemaMutation();

  const [step, setStep] = useState(1);
  const [dsType, setDsType] = useState<DsType>("search_index");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [schemaTab, setSchemaTab] = useState<"upload" | "editor">("upload");
  const [parsedSchema, setParsedSchema] = useState<{
    fields: { name: string; type: string }[];
  } | null>(null);
  const [editorContent, setEditorContent] = useState(DEFAULT_SCHEMA_JSON);
  const [analyzedFields, setAnalyzedFields] = useState<SchemaField[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [filterConfig, setFilterConfig] =
    useState<FilterConfig>(emptyFilterConfig);

  const [provider, setProvider] = useState("algolia");
  const [appId, setAppId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [indexName, setIndexName] = useState("");

  const [createdDs, setCreatedDs] = useState<DataSource | null>(null);
  const [verifyResult, setVerifyResult] =
    useState<VerifyConnectionResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [error, setError] = useState("");

  const isByo = dsType === "byo";
  const totalSteps = isByo ? 6 : 5;
  const labels = isByo ? BYO_LABELS : MANAGED_LABELS;

  const fieldOptions = useMemo(() => {
    const fromSchema = analyzedFields.map((f) => f.name).filter(Boolean);
    const fromVerify = schemaFieldNames(verifyResult?.schema);
    return Array.from(new Set([...fromSchema, ...fromVerify]));
  }, [analyzedFields, verifyResult]);

  const reset = useCallback(() => {
    setStep(1);
    setDsType("search_index");
    setName("");
    setDescription("");
    setSchemaTab("upload");
    setParsedSchema(null);
    setEditorContent(DEFAULT_SCHEMA_JSON);
    setAnalyzedFields([]);
    setIsAnalyzing(false);
    setAnalyzeError(null);
    setFilterConfig(emptyFilterConfig());
    setProvider("algolia");
    setAppId("");
    setApiKey("");
    setIndexName("");
    setCreatedDs(null);
    setVerifyResult(null);
    setVerifyError(null);
    setError("");
  }, []);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const handleClose = () => {
    onClose();
    reset();
  };

  const runAIAnalysis = async (fields: { name: string; type: string }[]) => {
    setIsAnalyzing(true);
    setAnalyzeError(null);
    try {
      const result = await analyzeSchema({
        fields: fields.map((f) => ({ name: f.name, type: f.type })),
      }).unwrap();
      setAnalyzedFields(result.fields);
    } catch {
      setAnalyzeError(
        "Schema analysis failed. You can still set attributes manually.",
      );
      setAnalyzedFields(
        fields.map((f) => ({
          ...f,
          searchable: f.type === "string",
          indexable: true,
        })),
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCreatePlatformManaged = async () => {
    setError("");
    try {
      const req: CreateDataSourceRequest = {
        name: name.trim(),
        ds_type: dsType,
        description: description || undefined,
        connection_config: {},
        schema_definition:
          analyzedFields.length > 0 ? { fields: analyzedFields } : undefined,
        filter_config: toFilterConfigPayload(filterConfig),
      };
      const result = await createDs(req).unwrap();
      setCreatedDs(result);
      setStep(5);
    } catch (e: unknown) {
      setError(unwrapError(e) === "Request failed" ? "Creation failed" : unwrapError(e));
    }
  };

  const handleCreateAndVerifyByo = async () => {
    setError("");
    setVerifyError(null);
    try {
      const req: CreateDataSourceRequest = {
        name: name.trim(),
        ds_type: "byo",
        description: description || undefined,
        provider,
        connection_config: {
          app_id: appId,
          api_key: apiKey,
          index_name: indexName,
        },
      };
      const created = await createDs(req).unwrap();
      setCreatedDs(created);
      try {
        const vResult = await verifyConn(created.dataSourceId).unwrap();
        setVerifyResult(vResult as unknown as VerifyConnectionResult);
      } catch {
        setVerifyError(
          "Connection verification failed. The data source was created but may need reconfiguration.",
        );
      }
      setStep(4);
    } catch (e: unknown) {
      setError(unwrapError(e) === "Request failed" ? "Creation failed" : unwrapError(e));
    }
  };

  const handleSaveByoFilters = async () => {
    if (!createdDs) {
      setError("Data source was not created. Go back and verify the connection.");
      return;
    }
    setError("");
    const payload = toFilterConfigPayload(filterConfig);
    if (payload) {
      try {
        const updated = await updateDs({
          id: createdDs.dataSourceId,
          body: { filter_config: payload },
        }).unwrap();
        setCreatedDs(updated);
      } catch (e: unknown) {
        setError(unwrapError(e));
        return;
      }
    }
    setStep(6);
  };

  const canGoNext = (): boolean => {
    if (step === 1) return name.trim().length > 0;
    if (!isByo) {
      if (step === 2)
        return parsedSchema !== null && parsedSchema.fields.length > 0;
      if (step === 3) return analyzedFields.length > 0;
      if (step === 4) return true;
      return false;
    }
    if (step === 2) return !!provider;
    if (step === 3)
      return (
        appId.trim().length > 0 &&
        apiKey.trim().length > 0 &&
        indexName.trim().length > 0
      );
    if (step === 4) return Boolean(createdDs);
    if (step === 5) return true;
    return false;
  };

  const handleNext = () => {
    setError("");
    if (step === 1) {
      if (!name.trim()) {
        setError("Please enter a data source name.");
        return;
      }
      setStep(2);
      return;
    }

    if (!isByo) {
      if (step === 2) {
        if (!parsedSchema || parsedSchema.fields.length === 0) {
          setError("Please provide a valid schema with at least one field.");
          return;
        }
        setStep(3);
        runAIAnalysis(parsedSchema.fields);
        return;
      }
      if (step === 3) {
        setStep(4);
        return;
      }
      if (step === 4) {
        handleCreatePlatformManaged();
        return;
      }
    } else {
      if (step === 2) {
        if (!provider) {
          setError("Please select a provider.");
          return;
        }
        setStep(3);
        return;
      }
      if (step === 3) {
        if (!appId.trim() || !apiKey.trim()) {
          setError("Application ID and API Key are required.");
          return;
        }
        handleCreateAndVerifyByo();
        return;
      }
      if (step === 4) {
        setStep(5);
        return;
      }
      if (step === 5) {
        handleSaveByoFilters();
      }
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const isFinalStep = step === totalSteps;

  const getNextLabel = (): string => {
    if (!isByo) {
      if (step === 2) return "Analyze with AI";
      if (step === 4) return isCreating ? "Creating…" : "Create Data Source";
    } else {
      if (step === 3)
        return isCreating || isVerifying
          ? "Connecting…"
          : "Connect & Verify";
      if (step === 4) return "Continue";
      if (step === 5) return isUpdating ? "Saving…" : "Save filters";
    }
    return "Continue";
  };

  const renderStep = () => {
    if (step === 1) {
      return (
        <StepSelectType
          name={name}
          onNameChange={setName}
          description={description}
          onDescriptionChange={setDescription}
          selectedType={dsType}
          onTypeChange={setDsType}
        />
      );
    }

    if (!isByo) {
      if (step === 2) {
        return (
          <StepDefineSchema
            schemaTab={schemaTab}
            onSchemaTabChange={setSchemaTab}
            parsedSchema={parsedSchema}
            onSchemaChange={setParsedSchema}
            editorContent={editorContent}
            onEditorContentChange={setEditorContent}
          />
        );
      }
      if (step === 3) {
        return (
          <StepAIAttributes
            fields={analyzedFields}
            onFieldsChange={setAnalyzedFields}
            isAnalyzing={isAnalyzing}
            analyzeError={analyzeError}
          />
        );
      }
      if (step === 4) {
        return (
          <StepQueryFilters
            value={filterConfig}
            onChange={setFilterConfig}
            fieldOptions={fieldOptions}
          />
        );
      }
      if (step === 5) {
        return (
          <StepConnectionDetails
            createdDs={createdDs}
            apiBaseUrl={apiBaseUrl}
            onUploadData={() => {
              if (createdDs) onOpenDetail(createdDs, "upload");
              handleClose();
            }}
            onViewAll={handleClose}
          />
        );
      }
    } else {
      if (step === 2) {
        return (
          <StepSelectProvider
            selectedProvider={provider}
            onProviderChange={setProvider}
          />
        );
      }
      if (step === 3) {
        return (
          <StepCredentials
            appId={appId}
            onAppIdChange={setAppId}
            apiKey={apiKey}
            onApiKeyChange={setApiKey}
            indexName={indexName}
            onIndexNameChange={setIndexName}
          />
        );
      }
      if (step === 4) {
        return (
          <StepVerifyIndex
            isVerifying={isVerifying || isCreating}
            verifyResult={verifyResult}
            verifyError={verifyError}
            indexName={indexName}
          />
        );
      }
      if (step === 5) {
        return (
          <StepQueryFilters
            value={filterConfig}
            onChange={setFilterConfig}
            fieldOptions={fieldOptions}
          />
        );
      }
      if (step === 6) {
        return (
          <StepBYOSuccess
            createdDs={createdDs}
            provider={provider}
            indexName={indexName}
            onViewDataSource={() => {
              if (createdDs) onOpenDetail(createdDs, "overview");
              handleClose();
            }}
            onViewAll={handleClose}
          />
        );
      }
    }
    return null;
  };

  const busy = isCreating || isVerifying || isUpdating;
  const showBackButton =
    step > 1 &&
    !isFinalStep &&
    !(isByo && step === 4 && (isCreating || isVerifying));
  const showNextButton = !isFinalStep;
  const showCancelButton = !isFinalStep;

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Data Source"
      width="lg"
      position="right"
    >
      <StepIndicator
        currentStep={step}
        totalSteps={totalSteps}
        label={labels[step] ?? ""}
      />

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {renderStep()}

      {showNextButton && (
        <WizardFooter>
          <div>
            {showBackButton && (
              <Button variant="ghost" onClick={handleBack}>
                {"\u2190 Back"}
              </Button>
            )}
          </div>
          <FooterActions>
            {showCancelButton && (
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canGoNext() || busy}
            >
              {getNextLabel()}
            </Button>
          </FooterActions>
        </WizardFooter>
      )}
    </SlideOver>
  );
};

export default CreateWizard;
